import { defineStore } from 'pinia'
import { useDataCache } from '#nuxt-multi-cache/composables'
import { normalizeCurrencyCode } from '~/utils/currency'
import { fetchCurrencies, fetchCartData, displayErrorMessage, getFetchApiErrorStatus, type CurrencyData } from '~/queries'

type State = {
  list: CurrencyData[] | null
  isSelecting: boolean
}

const currencyCodeCookieOptions = {
  maxAge: 31536000,
  decode: (value: string) => normalizeCurrencyCode(decodeURIComponent(value)),
}

export const useCurrencyCodeCookie = () =>
  useCookie<string | null>('currencyCode', currencyCodeCookieOptions)

export const useCurrencyStore = defineStore('currency', {
  state: (): State => ({
    list: null,
    isSelecting: false,
  }),
  getters: {
    code(): string | undefined {
      return this.selected?.code ?? normalizeCurrencyCode(useSiteStore().data?.currency.code) ?? undefined
    },
    roundUp(): number | undefined {
      return this.selected?.roundUp ?? useSiteStore().data?.currency.roundUp
    },
    selected(state): CurrencyData | null {
      if (!state.list?.length) return null

      const code = normalizeCurrencyCode(useCurrencyCodeCookie().value)

      return (
        state.list.find((currency) => currency.code === code) ??
        state.list.find((currency) => currency.default) ??
        state.list[0]
      )
    },
  },
  actions: {
    async fetch(revalidate: boolean = false) {
      if (this.list && !revalidate) return this.list

      const app = useNuxtApp()
      const siteStore = useSiteStore()
      const { siteId, domain } = useSite().site.value
      const locale = app.$i18n.locale.value
      try {
        const { value, addToCache } = await useDataCache(`currencies:${siteId}:${locale}`, app.ssrContext?.event)
        const list = value && !revalidate
          ? value as CurrencyData[]
          : await app.runWithContext(() => fetchCurrencies())
        if (!list.length) throw new Error('No currencies available')
        this.list = list
        if (!value || revalidate) await addToCache(list, [`currencies:${domain}`], 60)
      } catch (error) {
        // Resolve a known site default BEFORE pages construct price cache keys.
        // Never send an unvalidated cookie while labelling prices as the default.
        if (this.list?.length) return this.list
        await app.runWithContext(() => siteStore.fetch())
        this.list = []
      }
      return this.list
    },
    /**
     * Currency affects prices on every cached page, the same way a locale
     * switch does — so, like AppCountrySelect's language switch, apply it with
     * a full reload rather than patching individual stores.
     */
    async select(code: string) {
      if (this.isSelecting) return
      code = normalizeCurrencyCode(code) ?? ''
      if (!this.list?.some((currency) => currency.code === code)) return
      const cookie = useCurrencyCodeCookie()
      if (cookie.value === code) return

      // Read the persisted cart before changing the cookie: its gift-card
      // face values must never be relabelled as another currency.
      this.isSelecting = true
      try {
        const cartId = useCookie<string | null>('cartId').value
        if (cartId) {
          const { $toast, $i18n } = useNuxtApp()
          try {
            const cart = await fetchCartData({ id: cartId })
            if (cart.items.some((item) => item.type === 'gift_card') && cart.currency !== code) {
              $toast.error($i18n.t('page.gift-card.currency.cart-locked', { currency: cart.currency ?? this.code }))
              return
            }
          } catch (error) {
            if (getFetchApiErrorStatus(error) !== 404) {
              displayErrorMessage(error)
              return
            }
          }
        }
        cookie.value = code
        if (import.meta.client) {
          await nextTick()
          window.location.reload()
        }
      } finally {
        this.isSelecting = false
      }
    },
  },
})
